"use client";

import { EmblaOptionsType } from "embla-carousel";
import {
  Dispatch,
  RefObject,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import { CardCarouselProps, CoveoCard } from "./card-carousel.props";
import { useArticleContext } from "contexts/ArticleContext";
import { useNewsContext } from "contexts/NewsContext";
import { useEventContext } from "contexts/EventContext";
import { useStoryContext } from "contexts/StoriesContext";
import { TaxonomyFields } from "types/ContentHub/Taxonomy";
import { CoveoCardsFields } from "types/Coveo";
import {
  fetchCardsCarouselItems,
  getImageProps,
} from "src/utils/contentHubComponentsUtils";
import { Text } from "@sitecore-content-sdk/nextjs";
import Link from "next/link";
import Image from "next/image";
import CustomEmblaCarousel from "src/molecules/Embla/EmblaCarousel";
import fallbackImage from "src/assets/images/fallback-image.jpg";
import { ICONS } from "src/variables/icons";
import Button from "src/atoms/Button";
import { useLocale } from "next-intl";
import { firstNonEmpty } from "src/utils";

const registerOutsideInteractionHandler = (
  cardRefs: RefObject<(HTMLDivElement | null)[]>,
  setIsTouched: Dispatch<SetStateAction<boolean>>,
  setActiveIndex: Dispatch<SetStateAction<number | null>>,
  setCurrentCardIndex: Dispatch<SetStateAction<number | null>>,
) => {
  const handleOutsideInteraction = (event: MouseEvent | TouchEvent) => {
    const clickedInsideCard = cardRefs.current.some((ref) =>
      ref?.contains(event.target as Node),
    );

    if (!clickedInsideCard) {
      setIsTouched(false);
      setActiveIndex(null);
      setCurrentCardIndex(null);
    }
  };

  document.addEventListener("mousedown", handleOutsideInteraction);
  document.addEventListener("touchstart", handleOutsideInteraction);

  return () => {
    document.removeEventListener("mousedown", handleOutsideInteraction);
    document.removeEventListener("touchstart", handleOutsideInteraction);
  };
};

const getCTAType = (className: string | undefined, textColor: string) => {
  if (className) return className;
  return textColor == "text-white" ? "primary-reverse" : "primary";
};

export const Default = (props: CardCarouselProps) => {
  console.log("[Contextual CardCarousel] Default -->", props.fields);

  const { page, fields } = props;
  const isPageEditing = page.mode.isEditing;

  const id = props.params.RenderingIdentifier;
  const textColor = props?.params?.Styles?.includes("text-white")
    ? "text-white"
    : "";
  const options: EmblaOptionsType = {
    loop: true,
    active: true,
    slidesToScroll: 1,
    align: "start",
  };

  const [cards, setCards] = useState<CoveoCard[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const { article } = useArticleContext();
  const { news } = useNewsContext();
  const { event } = useEventContext();
  const { story } = useStoryContext();

  const taxonomyContext: TaxonomyFields | undefined =
    ((article || news || event || story) as TaxonomyFields) || undefined;

  const pageId =
    firstNonEmpty(article?.id, news?.id, event?.id, story?.id) ?? undefined;
  const locale = useLocale();

  useEffect(() => {
    let cancelled = false;
    const fields = props.fields as unknown as CoveoCardsFields;
    const isDateOnly = props?.params?.HideTimeInDateField == "1";

    fetchCardsCarouselItems(
      fields,
      {
        locale: locale == "en" ? "en-us" : locale,
        isDateOnly,
        pageSize: fields?.NumberOfResults?.value || 8,
        linkLabel: fields?.ShowMoreText?.value || "Show More",
      },
      taxonomyContext,
      pageId,
    )
      .then((result) => {
        if (!cancelled) {
          setCards(result);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [currentCardIndex, setCurrentCardIndex] = useState<number | null>(null);
  const [showUnderline, setShowUnderline] = useState(false);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [isTouched, setIsTouched] = useState(false);

  useEffect(() => {
    return registerOutsideInteractionHandler(
      cardRefs,
      setIsTouched,
      setActiveIndex,
      setCurrentCardIndex,
    );
  }, []);

  const handleTouchStart = (index: number) => {
    setIsTouched(true);
    setCurrentCardIndex(index);
  };

  return (
    <div
      className="cardcarousel-component w-full max-w-[1408px] px-4 mx-auto mb-(--global-component-spacing) pt-5"
      id={id ?? undefined}
      data-role="cardcarousel-component"
    >
      <div className="text-center">
        {(props.fields?.Title?.value || isPageEditing) && (
          <Text
            field={props.fields?.Title}
            tag={"h2"}
            className={`mt-5 mb-0 leading-[40.04px] lg:leading-[1.25] pb-5 z-1 after:content-[''] after:block after:clear-both after:w-[50%] lg:after:w-[17%] after:mx-auto after:pt-[15px] after:leading-[1.25] after:text-[3.2rem] after:border-b-2 after:border-teal ${
              props.params?.Styles?.includes("article-slider") &&
              textColor == ""
                ? "text-dark-black"
                : textColor
            }`}
            data-testid="carousel-title"
          />
        )}
        {(props.fields?.SubTitle?.value || isPageEditing) && (
          <Text
            field={props.fields?.SubTitle}
            tag={"p"}
            className={`font-light text-base md:text-[21px] leading-[1.4] mb-5 ${textColor}`}
            data-testid="carousel-subtitle"
          />
        )}
      </div>
      <div className="pt-0 pb-0 lg:px-15 relative">
        {!loading && cards.length === 0 && (
          <div>
            <p className={`text-xl text-center font-medium my-8 ${textColor}`}>
              {fields?.ResultsNotFoundText?.value}
            </p>
          </div>
        )}
        <CustomEmblaCarousel
          slides={cards.map((card, index) => {
            const imageProps = getImageProps(card.image, card.title);

            return (
              <div
                key={`${card.title}-${index}`}
                ref={(el) => {
                  if (el) cardRefs.current[index] = el;
                }}
                className={`wrapper p-0 m-[10px_8px] bg-white transition-all duration-300 ease-in-out relative rounded-[8px] shadow-[0px_11px_20px_rgba(0,0,0,0.1)] 
                            border
                             ${
                               isTouched && currentCardIndex === index
                                 ? "border-teal shadow-[0_2px_8px_1px_rgba(0,0,0,0.6)]"
                                 : "border-lighter-gray hover:border-teal hover:shadow-[0px_18px_20px_-4px_rgba(0,0,0,0.08)]"
                             }`}
                onClick={() => setCurrentCardIndex(index)}
                onTouchStart={() => {
                  handleTouchStart(index);
                }}
              >
                <Link
                  href={card.link}
                  aria-label={card.title}
                  className="absolute inset-0 z-1 rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ecolab-blue"
                >
                  <span className="sr-only">{card.title}</span>
                </Link>
                <div className="cardWrapper relative cursor-pointer pb-2.5">
                  <div
                    className={`imageBox relative h-px pb-[calc(0%+168px)] mb-0 rounded-md ${
                      isTouched ? "border-teal" : ""
                    }`}
                  >
                    {card.image ? (
                      <Image
                        {...imageProps}
                        alt={card.title}
                        className="object-cover absolute top-0 left-0 h-[168px] w-full max-w-full block rounded-tl-md rounded-tr-md"
                        loading={"lazy"}
                      />
                    ) : (
                      <Image
                        src={fallbackImage.src}
                        alt={card.title}
                        width={296}
                        height={168}
                        sizes={
                          "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 25vw"
                        }
                        quality={85}
                        priority={false}
                        className="object-cover absolute top-0 left-0 h-[168px] w-full max-w-full block rounded-tl-md rounded-tr-md"
                        loading={"lazy"}
                      />
                    )}
                  </div>

                  <div className="textWrapper px-5 pt-5 pb-4.25 bg-white rounded-md flex flex-col h-full">
                    <div className="subTitleBox min-h-[24px] mb-2.5">
                      {card.date ? (
                        <p className="font-normal text-base leading-[24px] tracking-normal text-dark-gray">
                          {card.date}
                        </p>
                      ) : (
                        <span className="invisible">placeholder</span>
                      )}
                    </div>

                    <h3
                      className={`titleBox text-[20px] font-normal md:text-2xl leading-[1.4] md:leading-[1.34] tracking-[-0.2px] h-[84px] md:h-[94px] mb-2.5 mt-0 mx-0 overflow-hidden ${
                        activeIndex === index
                          ? "text-ecolab-blue"
                          : "text-black"
                      }`}
                    >
                      {card.title}
                    </h3>

                    <div className="descriptionBox min-h-[72px] mb-3">
                      {card.desc ? (
                        <p
                          className="font-normal text-base leading-[24px] tracking-normal text-text-gray overflow-hidden text-ellipsis line-clamp-3"
                          title={`${card.desc.substring(0, 120)}...`}
                        >
                          {`${card.desc.substring(0, 120)}...`}
                        </p>
                      ) : (
                        <span className="invisible">placeholder</span>
                      )}
                    </div>
                    <div className="mt-auto -ml-1.5 relative z-2">
                      <Button
                        field={{
                          value: {
                            href: card.link,
                            text: card.linkLabel,
                            title: card.title,
                          },
                        }}
                        buttonType="tertiary"
                        ariaLabel={`Learn more about ${card.title}`}
                        jssIconField={{ value: ICONS.CHEVRON_RIGHT }}
                        iconPosition="right"
                        className={`customButton justify-end text-base leading-[28px] text-ecolab-blue no-underline cursor-pointer break-words px-1.5! m-0 min-h-0! min-w-0! flex-row-reverse 
                                  hover:underline focus-visible:underline focus:font-bold active:font-bold visited:font-bold font-bold! focus:underline! active:underline! focus:border-0! active:border-0! ${
                                    showUnderline ? "underline" : ""
                                  } 
                        `}
                        style={
                          showUnderline
                            ? {
                                textDecoration: "underline",
                                textDecorationSkipInk: "none",
                                WebkitTextDecorationSkip: "none",
                              }
                            : undefined
                        }
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation(); // Prevent card click when button is clicked
                          setShowUnderline(true);
                          setTimeout(() => setShowUnderline(false), 1000);
                        }}
                        data-role="link"
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          options={options}
          classNames="!basis-[100%] lg:!basis-[25%] md:!basis-[33.33%]"
          autoPlayInterval={0}
          enableAutoplay={false}
          componentName="CardCarousel"
          data-testid="carousel-card"
        />
      </div>
      {(props?.fields?.Link?.value || isPageEditing) && (
        <div className="w-full text-center">
          <Button
            field={props?.fields?.Link}
            size="default"
            buttonType={getCTAType(
              props?.fields?.Link?.value?.class,
              textColor,
            )}
            className={`z-1 mt-13  justify-self-center`}
            iconName={
              props?.fields?.Link?.value?.class ? "CHEVRON_BLUE_RIGHT" : ""
            }
            data-testid="carousel-cta"
            data-role="link"
          />
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// EventSlider variant
// ---------------------------------------------------------------------------

export const EventSlider = (props: CardCarouselProps) => {
  console.log("---->[ContextualCardCarousel] EventSlider", props.fields);

  const id = props.params.RenderingIdentifier;
  const textColor = props?.params?.Styles?.includes("text-white")
    ? "text-white"
    : "";
  const options: EmblaOptionsType = {
    loop: true,
    active: true,
    slidesToScroll: 1,
    align: "start",
  };
  const { page, fields } = props;

  const isPageEditing = page.mode.isEditing;
  const [cards, setCards] = useState<CoveoCard[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const { article } = useArticleContext();
  const { news } = useNewsContext();
  const { event } = useEventContext();
  const { story } = useStoryContext();

  const taxonomyContext: TaxonomyFields | undefined =
    ((article || news || event || story) as TaxonomyFields) || undefined;

  const pageId =
    firstNonEmpty(article?.id, news?.id, event?.id, story?.id) ?? undefined;

  const locale = useLocale();

  useEffect(() => {
    let cancelled = false;
    const fields = props.fields as unknown as CoveoCardsFields;
    const isDateOnly = props?.params?.HideTimeInDateField == "1";

    fetchCardsCarouselItems(
      fields,
      {
        locale: locale == "en" ? "en-us" : locale,
        isDateOnly,
        pageSize: fields?.NumberOfResults?.value || 8,
        linkLabel: fields?.ShowMoreText?.value || "Show More",
      },
      taxonomyContext,
      pageId,
    )
      .then((result) => {
        if (!cancelled) {
          setCards(result);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [currentCardIndex, setCurrentCardIndex] = useState<number | null>(null);
  const [showUnderline, setShowUnderline] = useState(false);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [isTouched, setIsTouched] = useState(false);

  useEffect(() => {
    return registerOutsideInteractionHandler(
      cardRefs,
      setIsTouched,
      setActiveIndex,
      setCurrentCardIndex,
    );
  }, []);

  const handleTouchStart = (index: number) => {
    setIsTouched(true);
    setCurrentCardIndex(index);
  };

  return (
    <div
      className={`cardcarousel-component bg-transparent w-full max-w-[1408px] px-4 mx-auto mb-(--global-component-spacing) pt-5`}
      id={id ?? undefined}
      data-role="cardcarousel-component"
    >
      <div className="text-center">
        {(props.fields?.Title?.value || isPageEditing) && (
          <Text
            field={props.fields?.Title}
            tag={"h2"}
            className={`mt-2 mb-10 tracking-[0.8px] leading-[40px] ${textColor}`}
            data-testid="eventslider-title"
          />
        )}
      </div>
      <div className="pt-0 pb-0 lg:px-15 relative">
        {!loading && cards.length === 0 && (
          <div>
            <p className={`text-xl text-center font-medium my-8 ${textColor}`}>
              {fields?.ResultsNotFoundText?.value}
            </p>
          </div>
        )}
        <CustomEmblaCarousel
          slides={cards.map((card, index) => {
            const imageProps = getImageProps(card.image, card.title);
            return (
              <div
                key={`${card.title}-${index}`}
                ref={(el) => {
                  if (el) cardRefs.current[index] = el;
                }}
                data-testid="event-slider-card"
                className={`wrapper p-0 m-[10px_8px] bg-white transition-[box-shadow,border-color] duration-300 ease-in-out relative rounded-lg shadow-[0px_4px_10px_rgba(0,0,0,0.1)]
                            border ${
                              isTouched && currentCardIndex === index
                                ? "border-teal shadow-[0_2px_8px_1px_rgba(0,0,0,0.6)]"
                                : "border-lighter-gray hover:border-teal hover:shadow-[0_2px_8px_1px_rgba(0,0,0,0.6)]"
                            }`}
                onClick={() => setCurrentCardIndex(index)}
                onTouchStart={() => handleTouchStart(index)}
              >
                <Link
                  href={card.link}
                  aria-label={card.title}
                  className="absolute inset-0 z-1 rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ecolab-blue"
                >
                  <span className="sr-only">{card.title}</span>
                </Link>
                <div className="cardWrapper relative cursor-pointer pb-2.5">
                  {card.image && (
                    <div className="imageBox relative h-[168px] mb-0 rounded-md">
                      <Image
                        {...imageProps}
                        className="object-cover absolute top-0 left-0 h-[168px] w-full max-w-full block rounded-tl-md rounded-tr-md"
                        alt={card.title}
                        loading={"lazy"}
                      />
                    </div>
                  )}
                  <div className="textWrapper px-5 pt-5 pb-4.25 bg-white rounded-md">
                    <div className="min-h-[64px] mb-2.5">
                      <h3
                        className={`text-[20px] leading-[28px] lg:text-[24px] lg:leading-[32px] font-normal tracking-[-0.2px] hover:text-ecolab-blue mb-2.5 line-clamp-2 ${
                          activeIndex === index
                            ? "text-ecolab-blue"
                            : "text-black"
                        }`}
                      >
                        {card.title}
                      </h3>
                    </div>

                    <div className="h-[28px] mb-2 flex items-center">
                      {card.subtitle ? (
                        <h4 className="text-[14px] leading-[21px] tracking-[0.4px] lg:text-[16px] lg:leading-[24px] font-medium text-ecolab-blue truncate">
                          {card.subtitle}
                        </h4>
                      ) : (
                        <span>&nbsp;</span>
                      )}
                    </div>

                    <div className="min-h-[42px]">
                      {card.date && (
                        <p className="font-bold text-[14px] leading-[20px] text-charcoal-blue mb-1.5 cursor-default">
                          {card.date}
                        </p>
                      )}
                    </div>
                    <div className="min-h-[40px] h-[38px] overflow-hidden">
                      {card.timezone && (
                        <p className="text-[13.6px] font-bold leading-[19px] text-charcoal-blue mb-2 cursor-default">
                          {card.timezone}
                        </p>
                      )}
                    </div>

                    <div className="min-h-[72px] mb-3">
                      {card.desc && (
                        <p className="text-base text-text-gray leading-[24px] line-clamp-3 overflow-hidden">
                          {card.desc.length > 120
                            ? `${card.desc.substring(0, 120)}...`
                            : card.desc}
                        </p>
                      )}
                    </div>

                    <div className="mt-auto -ml-1.5 relative z-2">
                      <Button
                        field={{
                          value: {
                            href: card.link,
                            text: card.linkLabel,
                            title: card.title,
                          },
                        }}
                        buttonType="tertiary"
                        ariaLabel={`Show More about ${card.title}`}
                        jssIconField={{ value: ICONS.CHEVRON_RIGHT }}
                        iconPosition="right"
                        tabIndex={0}
                        style={
                          showUnderline
                            ? {
                                textDecoration: "underline",
                                textDecorationSkipInk: "none",
                                WebkitTextDecorationSkip: "none",
                                WebkitTapHighlightColor: "transparent",
                              }
                            : undefined
                        }
                        className={`text-base justify-end  text-ecolab-blue leading-[22.8571px]
                                    hover:underline  focus-visible:underline
                                    focus:font-bold active:font-bold visited:font-bold font-bold!
                                    ios-active-underline focus:underline! active:underline! focus:border-0! active:border-0!
                                    ${showUnderline ? "underline" : ""}`}
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          setShowUnderline(true);
                          setTimeout(() => setShowUnderline(false), 1000);
                        }}
                        data-role="link"
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          options={options}
          classNames="!basis-[100%] lg:!basis-[25%] md:!basis-[33.33%]"
          autoPlayInterval={0}
          enableAutoplay={false}
          componentName="EventSlider"
        />
      </div>
      {(props.fields?.Link?.value || isPageEditing) && (
        <div className="text-center mt-10 mb-(--global-component-spacing)">
          <Button
            buttonType={getCTAType(
              props?.fields?.Link?.value?.class,
              textColor,
            )}
            field={props.fields?.Link}
            iconName={
              props.fields?.Link?.value?.class ? "CHEVRON_BLUE_RIGHT" : ""
            }
            tabIndex={0}
            ariaLabel={props.fields?.Link?.value?.text}
            className="justify-self-center focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black"
            data-testid="eventslider-cta"
            data-role="link"
          />
        </div>
      )}
    </div>
  );
};
